/**
 * The six products configured in Stripe, in one place.
 *
 * Lookup keys are permanent sync keys: Stripe matches prices by them, so a key
 * here must never be renamed without renaming it in Stripe first.
 */
export const PRICE_KEYS = {
  /** B2B | Unlimited Learning — seat-based yearly license. */
  businessUnlimited: "annual_license_yearly",
  /** B2B | Unlimited Learning with Coaching — add-on, one line per seat. */
  businessCoaching: "coaching_addon_yearly",
  /** B2C | Unlimited Learning — yearly, always one person. */
  individualUnlimited: "individual_unlimited_yearly",
  /** B2C | Unlimited Learning with Coaching — add-on, one person. */
  individualCoaching: "individual_coaching_addon_yearly",
  /** B2C | One Course Learning — one-time. */
  oneCourse: "single_course_onetime",
  /** B2C | Certification Learning — one-time, a certification's courses plus a certificate. */
  certification: "three_course_onetime",
} as const;

/** What the buyer bought. Also stored on orders and entitlements. */
export type PurchaseType =
  | "annual_license"
  | "individual_unlimited"
  | "single_course"
  | "certification";

/**
 * Courses a purchase covers, where the product alone decides it. Yearly plans
 * are unlimited, so 0. A certification's count is not fixed — it is however many
 * courses Training Pipeline lists for the chosen certification, so it is
 * resolved from the API at checkout instead of here.
 */
export function coursesIncludedFor(purchaseType: PurchaseType): number {
  return purchaseType === "single_course" ? 1 : 0;
}

/* ------------------------------------------------------------------ *
 * Individual (B2C) plan catalog shown on /start.
 * Prices are display strings; the real amounts live in Stripe against
 * the lookup keys above.
 * ------------------------------------------------------------------ */

export type PlanId = "one-course" | "certification" | "unlimited";

export interface Plan {
  id: PlanId;
  name: string;
  price: string;
  priceNote: string;
  body: string;
  priceLookupKey: string;
  mode?: "payment";
  purchaseType: PurchaseType;
  highlighted?: boolean;
}

export const PLANS: readonly Plan[] = [
  {
    id: "one-course",
    name: "One course",
    price: "$49",
    priceNote: "one-time",
    body: "Pick the single problem in front of you and start there.",
    priceLookupKey: PRICE_KEYS.oneCourse,
    mode: "payment",
    purchaseType: "single_course",
  },
  {
    id: "certification",
    name: "Certification",
    price: "$129",
    priceNote: "one-time",
    body: "Three courses that work together, plus a certificate you can show.",
    priceLookupKey: PRICE_KEYS.certification,
    mode: "payment",
    purchaseType: "certification",
  },
  {
    id: "unlimited",
    name: "Unlimited",
    price: "$349",
    priceNote: "per year",
    body: "Every course, all year, plus personalized support from a Development Pro.",
    priceLookupKey: PRICE_KEYS.individualUnlimited,
    purchaseType: "individual_unlimited",
    highlighted: true,
  },
];

export const DEFAULT_PLAN: PlanId = "unlimited";

/** Coaching is an add-on to the yearly Unlimited plan only, one person. */
export const COACHING = {
  lookupKey: PRICE_KEYS.individualCoaching,
  name: "Unlimited asynchronous coaching",
  price: "$499",
  priceNote: "per year",
  body: "For genuinely hard situations, when you want a real person in your corner.",
} as const;

/** Resolves a ?plan= search value, tolerating the legacy "three-course" name. */
export function toPlanId(value: unknown): PlanId {
  if (value === "three-course") return "certification";
  return PLANS.some((plan) => plan.id === value) ? (value as PlanId) : DEFAULT_PLAN;
}
